<script setup lang="ts">
import { ref } from 'vue';

import { DebugService } from '../services/DebugService';

const props = defineProps<{
  debugService: DebugService;
}>();

const isOpen = ref(false);

const togglePanel = () => {
  isOpen.value = !isOpen.value;
};
</script>

<template>
  <div
    class="debug-panel-container"
    :class="{ open: isOpen }"
  >
    <!-- Trigger Button -->
    <button
      class="debug-trigger"
      type="button"
      :aria-expanded="isOpen"
      aria-controls="debug-panel-content"
      @click="togglePanel"
    >
      debug
    </button>

    <!-- Panel Content -->
    <div
      id="debug-panel-content"
      class="debug-content"
    >
      <h3>Debug Options</h3>

      <div class="debug-option">
        <label for="toggle-collision-grid">
          <input
            id="toggle-collision-grid"
            :checked="props.debugService.state.showCollisionGrid"
            type="checkbox"
            @change="props.debugService.updateState('showCollisionGrid', ($event.target as HTMLInputElement).checked)"
          >
          Collision Grid
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-path-markers">
          <input
            id="toggle-path-markers"
            :checked="props.debugService.state.showPathMarkers"
            type="checkbox"
            @change="props.debugService.updateState('showPathMarkers', ($event.target as HTMLInputElement).checked)"
          >
          Path Markers
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-grid-lines">
          <input
            id="toggle-grid-lines"
            :checked="props.debugService.state.showGridLines"
            type="checkbox"
            @change="props.debugService.updateState('showGridLines', ($event.target as HTMLInputElement).checked)"
          >
          Grid Lines
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-destination-markers">
          <input
            id="toggle-destination-markers"
            :checked="props.debugService.state.showDestinationMarkers"
            type="checkbox"
            @change="props.debugService.updateState('showDestinationMarkers', ($event.target as HTMLInputElement).checked)"
          >
          Destination Markers
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-line-of-sight">
          <input
            id="toggle-line-of-sight"
            :checked="props.debugService.state.showLineOfSight"
            type="checkbox"
            @change="props.debugService.updateState('showLineOfSight', ($event.target as HTMLInputElement).checked)"
          >
          Line of Sight
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-time-display">
          <input
            id="toggle-time-display"
            :checked="props.debugService.state.showTimeDisplay"
            type="checkbox"
            @change="props.debugService.updateState('showTimeDisplay', ($event.target as HTMLInputElement).checked)"
          >
          Time Display
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-lighting">
          <input
            id="toggle-lighting"
            :checked="props.debugService.state.showLighting"
            type="checkbox"
            @change="props.debugService.updateState('showLighting', ($event.target as HTMLInputElement).checked)"
          >
          Lighting
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-attack-arcs">
          <input
            id="toggle-attack-arcs"
            :checked="props.debugService.state.showAttackArcs"
            type="checkbox"
            @change="props.debugService.updateState('showAttackArcs', ($event.target as HTMLInputElement).checked)"
          >
          Attack Arcs
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-hitboxes">
          <input
            id="toggle-hitboxes"
            :checked="props.debugService.state.showHitboxes"
            type="checkbox"
            @change="props.debugService.updateState('showHitboxes', ($event.target as HTMLInputElement).checked)"
          >
          Hitboxes
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-chunk-boundaries">
          <input
            id="toggle-chunk-boundaries"
            :checked="props.debugService.state.showChunkBoundaries"
            type="checkbox"
            @change="props.debugService.updateState('showChunkBoundaries', ($event.target as HTMLInputElement).checked)"
          >
          Chunk Boundaries
        </label>
      </div>
      
      <div class="debug-option">
        <label for="toggle-mouse-coordinates">
          <input
            id="toggle-mouse-coordinates"
            :checked="props.debugService.state.showMouseCoordinates"
            type="checkbox"
            @change="props.debugService.updateState('showMouseCoordinates', ($event.target as HTMLInputElement).checked)"
          >
          Mouse Tooltip (Coords)
        </label>
      </div>

      <div class="debug-option">
        <label for="toggle-edge-coordinates">
          <input
            id="toggle-edge-coordinates"
            :checked="props.debugService.state.showEdgeCoordinates"
            type="checkbox"
            @change="props.debugService.updateState('showEdgeCoordinates', ($event.target as HTMLInputElement).checked)"
          >
          Screen Edge (Coords)
        </label>
      </div>

      <div class="debug-footer">
        <button @click="props.debugService.reset()">
          Reset All
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.debug-panel-container {
  position: fixed;
  bottom: 10px;
  right: -300px;
  width: 300px;
  height: auto;
  max-height: 80vh;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  border-left: 2px solid #555;
  border-bottom: 2px solid #555;
  border-top: 2px solid #555;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  transition: right 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28);
  font-family:
    'Inter',
    system-ui,
    -apple-system,
    sans-serif;
  z-index: 1000000;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.5);
}

.debug-panel-container.open {
  right: 0;
}

.debug-trigger {
  position: absolute;
  bottom: 0;
  left: -60px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: #00ff00;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  cursor: pointer;
  border-left: 2px solid #555;
  border-top: 2px solid #555;
  border-bottom: 2px solid #555;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  border-right: none;
  font-family: inherit;
  user-select: none;
  transition:
    color 0.2s,
    background-color 0.2s;
}

.debug-trigger:focus-visible {
  outline: 2px solid #00ff00;
  outline-offset: -2px;
}

.debug-trigger:hover {
  color: #55ff55;
}

.debug-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.debug-content h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  border-bottom: 1px solid #444;
  padding-bottom: 8px;
  color: #aaa;
}

.debug-option {
  display: flex;
  align-items: center;
}

.debug-option label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  width: 100%;
  font-size: 15px;
}

.debug-option input {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #00ff00;
}

.debug-footer {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #444;
}

.debug-footer button {
  background: #333;
  color: #fff;
  border: 1px solid #555;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;
}

.debug-footer button:hover {
  background: #444;
}
</style>
